import { useCallback, useEffect, useRef, useState } from 'react';
import type { RoomMode, RoomSnapshot } from './types';

type Control =
  | { type: 'control.participant_gain.set'; room_id: string; participant_id: number; gain_db: number; final?: boolean }
  | { type: 'control.participant_mute.set'; room_id: string; participant_id: number; muted: boolean }
  | { type: 'control.program_include.set'; room_id: string; participant_id: number; included: boolean }
  | { type: 'control.room_mode.set'; room_id: string; mode: RoomMode };

export function useControlSocket(active: boolean) {
  const [snapshot, setSnapshot] = useState<RoomSnapshot>();
  const [snapshotReceivedAt, setSnapshotReceivedAt] = useState(0);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const socket = useRef<WebSocket | undefined>(undefined);
  const request = useRef(1);
  const retry = useRef<number | undefined>(undefined);
  const lastGain = useRef(new Map<number, number>());

  useEffect(() => {
    if (!active) return;
    let disposed = false;
    const connect = () => {
      const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${location.host}/api/v1/ws`);
      socket.current = ws;
      ws.onopen = () => { setConnected(true); setError(''); };
      ws.onclose = () => { setConnected(false); if (!disposed) retry.current = window.setTimeout(connect, 1200); };
      ws.onerror = () => setError('server_unreachable');
      ws.onmessage = event => {
        const value = JSON.parse(String(event.data)) as (RoomSnapshot | { type: 'control.rejected'; error_code?: string });
        if (value.type === 'room_snapshot') { setSnapshot(value); setSnapshotReceivedAt(Date.now()); ws.send(JSON.stringify({ type: 'snapshot_ack', state_revision: value.state_revision })); }
        if (value.type === 'control.rejected') setError(value.error_code ?? 'control_rejected');
      };
    };
    connect();
    return () => { disposed = true; if (retry.current) clearTimeout(retry.current); socket.current?.close(); };
  }, [active]);

  const send = useCallback((control: Control) => {
    if (socket.current?.readyState !== WebSocket.OPEN) { setError('server_unreachable'); return; }
    socket.current.send(JSON.stringify({ v: 1, request_id: String(request.current++), ...control }));
  }, []);
  const setGain = useCallback((room_id: string, participant_id: number, gain_db: number, final = false) => {
    setSnapshot(current => current ? { ...current, participants: current.participants?.map(participant => participant.participant_id === participant_id ? { ...participant, gain_db } : participant) } : current);
    const now = performance.now(); const previous = lastGain.current.get(participant_id) ?? 0;
    if (!final && now - previous < 40) return;
    lastGain.current.set(participant_id, now);
    send({ type: 'control.participant_gain.set', room_id, participant_id, gain_db, final });
  }, [send]);
  return { snapshot, snapshotReceivedAt, connected, error, setError, send, setGain };
}
